import { movies } from "@/mocks/movies";
import { tvShows } from "@/mocks/tvshows";
import { movieGenres, tvGenres } from "@/mocks/genres";
import type { GenreOption, MediaItem, MediaType } from "@/lib/types";

export const catalog: MediaItem[] = [...movies, ...tvShows];

export const regions = ["全部", "中国大陆", "中国香港", "美国", "日本", "韩国", "英国", "法国"];

export const years = ["全部", "2024", "2023", "2022", "2021", "2020"];

export function typeLabel(type: MediaType): string {
  return type === "movie" ? "电影" : "电视剧";
}

export function getGenresByType(type: MediaType): GenreOption[] {
  return type === "movie" ? movieGenres : tvGenres;
}

export function getMediaById(id: string): MediaItem | undefined {
  return catalog.find((item) => item.id === id);
}

export interface FilterParams {
  type: MediaType;
  genre?: string;
  region?: string;
  year?: string;
  sort?: string;
}

export function filterCatalog(params: FilterParams): MediaItem[] {
  const { type, genre = "all", region = "全部", year = "全部", sort = "latest" } = params;
  const genreMap = getGenresByType(type);
  const genreLabel = genreMap.find((g) => g.key === genre)?.label;

  let list = catalog.filter((item) => item.type === type);

  if (genreLabel) {
    list = list.filter((item) => item.genres.includes(genreLabel));
  }
  if (region && region !== "全部") {
    list = list.filter((item) => item.region === region);
  }
  if (year && year !== "全部") {
    list = list.filter((item) => String(item.year) === year);
  }

  if (sort === "hot") {
    list = [...list].sort((a, b) => b.heat - a.heat);
  } else if (sort === "rating") {
    list = [...list].sort((a, b) => b.rating - a.rating);
  } else {
    list = [...list].sort((a, b) => b.year - a.year);
  }

  return list;
}

export function searchCatalog(keyword: string): MediaItem[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return catalog.filter((item) => {
    return (
      item.title.toLowerCase().includes(q) ||
      item.genres.some((g) => g.toLowerCase().includes(q)) ||
      item.region.toLowerCase().includes(q) ||
      item.director.toLowerCase().includes(q) ||
      item.tags.some((t) => t.toLowerCase().includes(q))
    );
  });
}

export function getRelated(item: MediaItem, limit = 4): MediaItem[] {
  const scored = catalog
    .filter((other) => other.id !== item.id)
    .map((other) => {
      let score = 0;
      if (other.type === item.type) score += 1;
      score += other.genres.filter((g) => item.genres.includes(g)).length * 3;
      score += other.tags.filter((t) => item.tags.includes(t)).length * 2;
      if (other.region === item.region) score += 1;
      return { other, score };
    })
    .filter((entry) => entry.score > 1)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((entry) => entry.other);
}

export function getHotList(limit = 6): MediaItem[] {
  return [...catalog].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getLatestList(limit = 8): MediaItem[] {
  return [...catalog].sort((a, b) => b.year - a.year || b.heat - a.heat).slice(0, limit);
}

export function getHeroList(limit = 5): MediaItem[] {
  const ids = ["mv-001", "tv-002", "mv-006", "tv-001", "mv-003"];
  return ids
    .map((id) => getMediaById(id))
    .filter((item): item is MediaItem => Boolean(item))
    .slice(0, limit);
}